/**
 * client/src/pages/security-centre/lib/protection-level-copy.ts
 *
 * Every user-facing string the protection card says ABOUT A LEVEL: what it
 * admits, why the session is at it, and how far Level 3 of 4 actually
 * is. Extracted from `ProtectionLevelSummary.tsx` (300-line modularity rule)
 * so each claim is unit-testable without mounting the card, and so the copy
 * sits next to the reasoning that constrains it.
 *
 * The reason and route lines are FUNCTIONS of the live route facts, not a
 * static table. Two rounds of review found that a static per-level sentence
 * is false for a real population at every level — see `levelReason` and
 * `lockedRouteNote` for the specific cases. A level alone does not determine
 * why you are at it or how you get out.
 *
 * HONEST FRAMING (Friction Rule clause 2, CLAUDE.md §AAL Thresholds): every
 * string states a CAPABILITY or a fact about what a session PROVED. None
 * claims a live human is present, that deepfakes are resisted, or that an
 * identity has been verified.
 */

import { PROTECTION_LEVELS } from "@/lib/terminology";
import type { CognitiveRoute, HardwareRoute } from "@/app/aal3-route-status";
import type { ProtectionNextStep } from "./protection-next-step";
import { PRODUCT_NAME } from "@shared/brand";

export type Aal = "AAL1" | "AAL2" | "AAL3";

export const LEVEL_ORDER: readonly Aal[] = ["AAL1", "AAL2", "AAL3"];

/**
 * What each level's real server-side gates actually admit — plain English,
 * no jargon, no claim that a live human is present.
 *
 * The vault wording is narrower than it looks and was corrected twice. Vault
 * DOCUMENT content (`vault-documents.routes.ts`) mounts `requireAuth` only,
 * and vault IDENTIFIERS gate on each row's own `minimum_aal`, whose column
 * default is `"AAL2"` — so an ordinary session opens both. The ONLY AAL3
 * vault surface is the org bank-account envelope
 * (`org-vault/account-envelope.ts`), which is why Level 3 names that
 * specifically rather than "your vault" generally. An earlier draft demoted
 * AAL2 to "browse your vault list", which understated it in the same way the
 * original overstated it.
 */
export const LEVEL_GRANTS: Record<Aal, string> = {
  AAL1: "Sign in and look around. No passkey proven, so nothing sensitive.",
  AAL2:
    "Everyday use: send and sign messages, manage connections and deals, open your vault " +
    "documents and saved details.",
  AAL3:
    "Money-level actions for this session — adding and co-signing bank accounts, opening org " +
    "bank-account records, preparing and approving payout files — " +
    "plus requesting org API access.",
};

/**
 * What the levels ABOVE `aal` admit — the honest "not yet" list.
 *
 * NOT `LEVEL_GRANTS.AAL3`, which is what an earlier build hardcoded. At AAL1
 * that told the user they could not move money while saying nothing about the
 * everyday use they are ACTUALLY blocked from one rung up — so the level they
 * were standing next to was the one the card skipped. Deriving it from
 * `LEVEL_ORDER` is also what makes this module's own header claim true ("the
 * locked list can never drift from the level that would unlock it").
 *
 * Returns `[]` at Level 3, where nothing is withheld.
 */
export function lockedGrants(aal: Aal): readonly string[] {
  return LEVEL_ORDER.slice(LEVEL_ORDER.indexOf(aal) + 1).map((level) => LEVEL_GRANTS[level]);
}

export const LEVEL_TONE: Record<Aal, "neutral" | "blue" | "emerald"> = {
  AAL1: "neutral",
  AAL2: "blue",
  AAL3: "emerald",
};

export interface LevelRouteContext {
  hardware: HardwareRoute;
  cognitive: CognitiveRoute;
}

/**
 * WHY the session is at this level — the fact the server actually acted on.
 *
 * Every branch here exists because a flat sentence was demonstrably false for
 * a real population:
 *
 * - **AAL1** splits on whether any credential exists. `updateAAL(userId,
 *   "AAL1")` is written DURABLY by the anomaly-critical downgrade path
 *   (the OSINT downgrade it replaced is deleted) and by recovery completion
 *   (`recovery/session-reset.ts`), and `capSessionAalAtLogin` takes the min
 *   of the profile and the login capability — so a downgraded account with a
 *   perfectly good passkey lands here, and "you haven't proven a passkey yet"
 *   is false for them.
 * - **AAL2** must NOT say the credential is the limitation. With an attested
 *   key on the account this session simply didn't use it — `aal3-route-status
 *   .ts` documents that exact state as the reported defect, and
 *   `HardwareRoute.certified` is defined as "a certified key exists, so this
 *   session didn't use it". An earlier draft added "and it isn't one that
 *   reaches Level 3 on its own", which is precisely the false
 *   half.
 * - **AAL3** names the bases as alternatives and asserts none: the basis
 *   (`session.aal3Basis`) is never projected to the client, AND a ceremony
 *   need not have run at all, since `loginCapability` (`aal-cap.ts`) grants
 *   AAL3 at sign-in for an attested credential (the memory-bank basis was
 *   removed from sign-in by #3434). The cognitive clause is included ONLY
 *   when that route is not reporting itself unavailable — and it now always
 *   reports unavailable, since cognitive-first promotion is retired outright
 *   (2026-08-31 security tier ladder spec, module 06 R8), so naming it
 *   unconditionally would offer a basis nobody can be on.
 */
export function levelReason(aal: Aal, ctx: LevelRouteContext): string {
  if (aal === "AAL1") {
    return ctx.hardware === "none"
      ? "No passkey is registered on this account yet."
      : `A passkey is registered, but this session is at ${PROTECTION_LEVELS.AAL1.label} — completing an account recovery, or a security freeze, resets it here.`;
  }
  if (aal === "AAL2") {
    return ctx.hardware === "certified"
      ? "You signed in with a passkey. Your attested hardware key is on the account — this session just didn't use it."
      : "You signed in with a passkey.";
  }
  const bases =
    ctx.cognitive === "unavailable"
      ? "an attested hardware key, or two of your passkeys"
      : "an attested hardware key, a memory vault at baseline, or two of your passkeys";
  return `This session proved one of the strongest checks — ${bases} — either when you signed in or by verifying here.`;
}

/**
 * How far away Level 3 of 4 actually is, keyed to the SAME resolved
 * route the button below runs — never a flat "it takes one extra
 * verification". That flat wording was wrong for the commonest below-Level-3
 * account, where the real route is add a passkey → wait out
 * `DUAL_LEG_MIN_CREDENTIAL_AGE_MS` (24h) → a two-leg ceremony.
 *
 * AAL1 is handled first and never falls through to the AAL3 routes: it has a
 * level of its own to reach before Level 3 is even the question, and the
 * remedy is a step-up ceremony (`beginStepUp({targetAal:"AAL2"})`), NOT
 * signing in again — for a durably-downgraded account (see `levelReason`) a
 * fresh login re-caps straight back to AAL1, so "sign in with a passkey"
 * would be a loop.
 *
 * `null` (a route fact that hasn't resolved) makes NO route claim at all —
 * only the session-boundary fact, which is true regardless. Same three-valued
 * posture as `useAal3RouteStatus` itself: say nothing rather than guess.
 */
export function lockedRouteNote(
  aal: Aal,
  nextStep: ProtectionNextStep | null,
  ctx: LevelRouteContext,
): string {
  const boundary = `${PROTECTION_LEVELS.AAL3.label} lasts for the session it's proven in.`;
  if (aal === "AAL1") {
    const first =
      ctx.hardware === "none"
        ? `Register a passkey to reach ${PROTECTION_LEVELS.AAL2.label} first.`
        : `Verify your passkey to return to ${PROTECTION_LEVELS.AAL2.label} first.`;
    return `${first} ${boundary}`;
  }
  if (nextStep === null) return boundary;
  switch (nextStep.kind) {
    case "verify-hardware":
      return `Reaching it takes one more verification, here. ${boundary}`;
    case "verify-two-key":
      // Two taps, one ceremony — the component's own running label says "Now
      // tap your second passkey…", so "one more verification" alone would
      // surprise at the second prompt.
      return `Reaching it takes one more verification here — two taps, one after the other. ${boundary}`;
    case "two-key-waiting":
      return `Reaching it takes one more verification, once your newest passkey is old enough. ${boundary}`;
    case "add-second-passkey":
      return `Reaching it means adding a second passkey, waiting 24 hours, then verifying with both. ${boundary}`;
    case "master-held":
      // #3268 review round 1 MAJOR 2: no verification here can succeed — the
      // ONLY honest remedy is the master transfer control the card links to.
      return `Your master passkey is on hold after a security incident, so no verification here can complete yet. ${boundary}`;
  }
}

/**
 * The one thing this card could otherwise state WRONGLY about an account that
 * holds a real security key: that the key was assessed and found uncertified.
 *
 * A credential enrolled while the FIDO MDS registry was unreachable carries
 * `mds_verified = false` because the check never ran, not because it ran and
 * failed (`server/logic/webauthn/attestation-reverify.ts` re-runs it on a
 * 10-minute sweep and flips the row when the registry comes back). Every other
 * line here reads that flag, so without this note the card silently converts
 * "we could not check" into "your key does not qualify" — and sends someone to
 * buy a second key they may not need (Friction Rule clause 2).
 *
 * Returns `null` for every other route state, including `uncertified-only`:
 * there the verdict IS established and the existing copy is accurate.
 */
export function hardwareCheckPendingNote(hardware: HardwareRoute): string | null {
  if (hardware !== "check-pending") return null;
  return `One of your security keys hasn't been checked against the manufacturer registry yet — that check was unreachable when you added it, and ${PRODUCT_NAME} retries automatically. Until it lands, this key can't unlock ${PROTECTION_LEVELS.AAL3.label} on its own — it can still serve as one of the two passkeys in a two-key check — and none of this is a verdict on the key itself.`;
}
